import { useQuery } from 'react-query';
import {
	getMeetingById,
	getMeetings,
	getAllMeetings,
	getRecordingByMeetingId,
	getOngoingAssessmentDetails,
	getMeetingScoreCard,
	getSampleAssessmentByMeetingId,
	getAllMeetingsDetail,
	getUserPreferenceMeetings,
	getAllMockRoundTemplates,
} from 'src/apis/meeting';
import {
	GetMeetingByIdAPIResponse,
	GetMeetingsAPIResponse,
	GetOngoingAssessmentDetailsAPIResponse,
	GetAllMeetingsAPIResponse,
	GetMeetingScoreCardAPIResponse,
	FilterQueryInput,
	GetSampleAssessmentByMeetingIdAPIResponse,
	GetAllMeetingsDetailAPIResponse,
	GetPreferenceMeetingsAPIResponse,
	GetAllMockRoundTemplatesAPIResponse,
} from 'types';
import { GetMeetingRecordingByIdAPIResponse } from 'types';

export const useGetAllMockRoundTemplatesQuery = (
	data?: { hiringAssessmentId?: string; token?: string },
	onComplete?: (data: GetAllMockRoundTemplatesAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllMockRoundTemplates', data?.hiringAssessmentId, data?.token],
		queryFn: async () =>
			(await getAllMockRoundTemplates({
				hiringAssessmentId: data?.hiringAssessmentId,
				token: data?.token,
			})) as GetAllMockRoundTemplatesAPIResponse,
		staleTime: 5000,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetMeetingByIdQuery = (
	id?: string,
	onComplete?: (data: GetMeetingByIdAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getMeetingById', id],
		queryFn: async () => (await getMeetingById(id ?? '')) as GetMeetingByIdAPIResponse,
		staleTime: 5000,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
export const useGetOngoingAssessmentDetails = (
	id?: string,
	onComplete?: (data: GetOngoingAssessmentDetailsAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getOngoingAssessmentDetails', id],
		queryFn: async () => (await getOngoingAssessmentDetails(id ?? '')) as GetOngoingAssessmentDetailsAPIResponse,
		staleTime: 5000,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetAllMeetings = (
	filterData: FilterQueryInput,
	onComplete?: (data: GetAllMeetingsAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllMeetings', filterData.filterQuery],
		queryFn: async () => (await getAllMeetings(filterData)) as GetAllMeetingsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetMeetingRecordingByMeetingIdQuery = (
	id?: string,
	onComplete?: (data: GetMeetingRecordingByIdAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getRecordingByMeetingId', id],
		queryFn: async () => (await getRecordingByMeetingId(id ?? '')) as GetMeetingRecordingByIdAPIResponse,
		staleTime: 0,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetUserPreferenceMeetingsQuery = (
	data?: { hiringAssessmentId?: string; token?: string },
	onComplete?: (data: GetPreferenceMeetingsAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getUserPreferenceMeetings', data?.hiringAssessmentId, data?.token],
		queryFn: async () =>
			(await getUserPreferenceMeetings({
				hiringAssessmentId: data?.hiringAssessmentId,
				token: data?.token,
			})) as GetPreferenceMeetingsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
export const useGetMeetingsQuery = (onComplete?: (data: GetMeetingsAPIResponse) => void, handleError?: () => void) => {
	return useQuery({
		queryKey: ['getMeetings'],
		queryFn: async () => (await getMeetings()) as GetMeetingsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
export const useGetMeetingScoreCard = (
	meetingId: string,
	onComplete?: (data: GetMeetingsAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getMeetingScoreCard'],
		queryFn: async () => (await getMeetingScoreCard(meetingId)) as GetMeetingScoreCardAPIResponse,
		staleTime: 0,
		enabled: !!meetingId,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetSampleAssessmentByMeetingIdQuery = (
	id?: string,
	onComplete?: (data: GetSampleAssessmentByMeetingIdAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getSampleAssessmentByMeetingId', id],
		queryFn: async () => (await getSampleAssessmentByMeetingId(id ?? '')) as GetSampleAssessmentByMeetingIdAPIResponse,
		staleTime: 0,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};

export const useGetAllMeetingsDetail = (
	handler: string,
	onComplete?: (data: GetAllMeetingsDetailAPIResponse) => void,
	handleError?: () => void,
) => {
	return useQuery({
		queryKey: ['getAllMeetingsDetail', handler],
		queryFn: async () => (await getAllMeetingsDetail(handler)) as GetAllMeetingsDetailAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
		onError() {
			handleError && handleError();
		},
	});
};
